import { Reducer } from 'redux';
import deepmerge from 'deepmerge';
import { StudentActionTypes } from '../types';
import { DocStatusType, IStudentConfig, OptionStatusType, RegionConfigType, SectionStatusType } from '../../../types';
import { FollowUpDocStatusType } from 'api-paths-cas';
import { reminderNotificationsConfig } from 'configuration-data';
export const defaultConfig: IStudentConfig = {
  id: '',
  university_name: 'Staging',
  university_code: 'STG',
  teamName: 'CAS team',
  registerTitleLine1: 'Register here to begin your',
  registerTitleLine2: ' CAS document application',
  customParentalConsentFormLink: '',
  otherGenderEnabled: true,
  tuitionPostDiscount: false /* university is supplying tuition fee post discount fee */,
  campuses: [],
  studentAutomatedChatReply:
    'Thank you for your message, we will endeavour to reply within 48 hours. Please ensure you have clearly outlined your question so our team can provide a satisfactory answer.',
  depositInstructionLink: '',
  configName: '',
  configDesc: '',
  allPartners: [],
  partnerIds: [],
  allImmigrationProviders: [],
  defaultQuestions: [],
  trapQuestions: [],
  type: RegionConfigType.DEFAULT,
  docSectionStatus: SectionStatusType.ACTIVE,
  finDocSectionStatus: SectionStatusType.ACTIVE,
  interviewSectionStatus: SectionStatusType.ACTIVE,
  historySectionStatus: SectionStatusType.ACTIVE,
  financeSectionStatus: SectionStatusType.ACTIVE,
  followUpDocSectionStatus: SectionStatusType.ACTIVE,
  beforeArrivalDocAreaStatus: SectionStatusType.ACTIVE,
  afterArrivalDocAreaStatus: SectionStatusType.ACTIVE,
  scheduledInterviewsSectionStatus: SectionStatusType.INACTIVE,
  recordedInterviewSectionStatus: SectionStatusType.INACTIVE,
  beforeArrivalDocuments: {
    customDocuments: {},
    studentRefusalLetter: FollowUpDocStatusType.REQUIRED,
    visaApplicationPdfCopy: FollowUpDocStatusType.REQUIRED,
    travelTicket: FollowUpDocStatusType.REQUIRED,
    visaVignette: FollowUpDocStatusType.REQUIRED,
    immigrationStatusShareCode: FollowUpDocStatusType.REQUIRED,
    immigrationStatusPdf: FollowUpDocStatusType.REQUIRED,
  },
  afterArrivalDocuments: {
    customDocuments: {},
    biometricResidencePermit: FollowUpDocStatusType.REQUIRED,
    stampedVisaVignette: FollowUpDocStatusType.REQUIRED,
  },
  documents: {
    customDocuments: {},
    studentVisas: {
      studentVisa: DocStatusType.REQUIRED,
      cas: DocStatusType.REQUIRED,
      biometric: DocStatusType.REQUIRED,
    },
    cas: DocStatusType.REQUIRED,
    passport: DocStatusType.REQUIRED,
    academicTranscript: DocStatusType.REQUIRED,
    academicCertificate: DocStatusType.REQUIRED,
    visaRefusals: DocStatusType.REQUIRED,
    nonUkVisaRefusals: DocStatusType.REQUIRED,
    otherVisas: {
      visas: DocStatusType.REQUIRED,
      biometric: DocStatusType.REQUIRED,
    },
    parentalConsent: DocStatusType.REQUIRED,
    birthCertificate: DocStatusType.REQUIRED,
    tbTest: DocStatusType.REQUIRED,
    atas: DocStatusType.REQUIRED,
    englishTest: DocStatusType.REQUIRED,
    englishQualification: DocStatusType.REQUIRED,
    customRequest: DocStatusType.REQUIRED,
    postStudyLetter: DocStatusType.REQUIRED,
  },
  financeDocuments: {
    deposit: DocStatusType.REQUIRED,
    birthCertificate: DocStatusType.REQUIRED,
    familySponsorship: {
      bankStatement: DocStatusType.REQUIRED,
      parentLetter: DocStatusType.REQUIRED,
      termDeposit: DocStatusType.REQUIRED,
      verificationBankLetter: DocStatusType.REQUIRED,
      secondaryBankStatement: DocStatusType.REQUIRED,
    },
    evidenceOfIncome: {
      curriculumVitae: DocStatusType.REQUIRED,
      employmentReference: DocStatusType.REQUIRED,
      recentEmploymentContract: DocStatusType.REQUIRED,
      recentPayslip: DocStatusType.REQUIRED,
    },
    governmentSponsorship: DocStatusType.REQUIRED,
    companySponsorship: DocStatusType.REQUIRED,
    scholarshipSponsorship: DocStatusType.REQUIRED,
    educationSponsorship: DocStatusType.REQUIRED,
    customRequest: DocStatusType.REQUIRED,
    other: DocStatusType.REQUIRED,
    customDocuments: {},
  },
  otherOptions: {
    visaSupport: OptionStatusType.INACTIVE,
  },
  scheduledInterviews: { calendlyEventType: { id: null } },
  sections: {
    CASChecklist: {
      label: 'Checklist',
      description: '',
      summaryLabel: 'Checklist Summary',
      summary:
        'On this page you can edit the answers you have provided to the checklist, or resume the process if you are yet to complete it.',
    },
    GeneralDocument: {
      label: 'General Documents',
      description: 'Please upload or review document requirements as requested',
      summaryLabel: 'General Documents',
      summary:
        'Below is a list of academic, travel and other documents associated with your CAS and visa application. This list is based on the answers you provided to the CAS Checklist questions.',
    },
    FinanceDocuments: {
      label: 'Financial Documents',
      description: 'Please upload or review document requirements as requested',
      summaryLabel: 'Proving your finances',
      summary:
        'As part of this CAS application or your upcoming visa application you may need to provide documents that prove you have access to the required #TotalFundingToShow in expenses as outlined in the financial plan section. <p>Click ‘How to Prove’ or ‘Please Review‘ as requested to see the specific requirements for each document.</p>',
    },
    FollowUpDocuments: {
      label: 'Follow-up Documents',
      description: 'Please upload or review document requirements as requested',
      summaryLabel: 'Follow-up Documents',
      summary: 'Please read and complete the tasks below.',
    },
    PreCASInterview: {
      label: 'Pre-CAS Questionnaire',
      description: 'Answer a few questions about your upcoming overseas study',
      summaryLabel: 'Pre-CAS Questionnaire',
      summary: 'Below is a series of questions built to evaluate credibility for a CAS to be issued',
    },
    ScheduledInterviews: {
      description: 'Please review this section to finalize any interview requirements',
      label: 'Interviews',
    },
    ReceiveCAS: {
      label: 'Receive CAS Document',
      description: 'Your CAS will not be issued until the above tasks are completed',
    },
    VisaApplication: {
      label: 'Start Visa Application',
      description: 'Begin the visa and immigration process',
    },
    Registration: {
      label: '15th of January',
      description: 'On-campus registration',
    },
  },
  Checklist: {
    defaultOverrides: {
      core: {},
      personalInfo: {},
    },
    Landing: {
      title: 'Welcome to the CAS preparation process',
      blurb: `In this process you will be asked a series of questions and given a recommended set of documents to be uploaded in advance of your CAS application review by the university.
        <br />
        <br />
        The list of documents and guidelines provided in this process do not constitute legal advice, the purpose is to speed up your preparation.
        <br />
        <br />
        Any official visa and immigration advice will be provided as needed by our university staff and/or an assigned solicitor. If you receive advice from these parties, please ensure you follow it carefully.
      `,
    },
    InfoProcessing: {
      title: 'Processing of Information',
      blurb:
        'I understand that the CAS preparation process will require the collection of personal and sensitive information that belongs to me. This data will be processed by the university and approved representatives for the purpose of my CAS application and enrolment.',
    },
    PersonalInfo: {
      title: 'Section 1 - Personal Information',
      shortTitle: 'Personal Information',
      SectionStart: {
        warning:
          '<strong>Note:</strong> The requested information may be used in official documents. Please ensure you enter accurate information.',
      },
      Page1: {
        title: 'Names and Aliases',
        hidden: false,
        GivenName: {
          label: 'Given Name',
          subLabel: 'Given names must match your passport exactly, including any middle names.',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        MiddleName: {
          label: 'Middle Name',
          subLabel:
            'Include all middle names as they appear on your passport if you have any, otherwise leave this blank.',
          visible: false,
          mandatory: false,
          defaultValue: '',
          helpText: '',
        },
        FamilyName: {
          label: 'Family Name',
          subLabel: 'Family name must match your passport exactly.',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'If you only have one name, enter it here and type <code>-</code> as your given name.',
        },
        HasOtherNames: {
          label: 'In addition to the names already provided, are you now or have you ever been known by another name?',
          visible: true,
          mandatory: true,
          helpText: '',
        },
        OtherNameFamilyName: {
          helpText: '',
          subLabel: 'Family name must match your passport exactly.',
        },
        OtherNameGivenName: {
          helpText: '',
        },
        OtherNameReason: {
          helpText:
            '<span>Please provide context for this name.<br /></br />If you changed your name please include the date it was changed.',
        },
      },
      Page2: {
        title: 'Gender, Birth and Nationality',
        hidden: false,
        Nationality: {
          label: 'Nationality',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText:
            'Enter your nationality/citizenship as per the passport or travel document you will use for your visa application. <br> You will be asked about additional or previous nationalities at the end of this section.',
        },
        HasOtherNationality: {
          label: 'Do you currently hold, or have you ever held, any other nationality or citizenship?',
          visible: true,
          mandatory: true,
          defaultValue: undefined,
          helpText: 'If you have dual nationality or citizenship, select “yes”',
        },
        OtherNationality: {
          label: 'Other Nationality',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        IsCountryOfNationality: {
          label: 'Will you be applying for your student visa in the same country as your declared nationality?',
          defaultValue: undefined,
          visible: true,
          mandatory: true,
          helpText:
            'Please confirm whether you intend to submit your student visa application in the country you have declared as your nationality above. This question refers to the location of your visa application',
        },
        AppliedForEUSS: {
          label: 'Have you been granted settled or pre-settled status in the UK under the EU Settlement Scheme (EUSS)?',
          defaultValue: '',
          helpText:
            'The EUSS is a Home Office scheme, which allows EU/EEA nationals and their family members who were residents in the UK by the 1st of January 2020 to apply to continue living in the UK.',
        },
        GrantedEUSSStatus: {
          label: 'Which status have you been granted?',
          defaultValue: '',
          helpText: '',
        },
        CountryOfVisaApplication: {
          label: 'In which country will you make your visa application?',
          mandatory: true,
          visible: true,
        },
        ReasonWhyNotCountryOfNationality: {
          label: 'Please tell us why you are applying from a country other than the country of your nationality?',
          defaultValue: '',
          helpText: '',
        },
        OtherNationalityCurrent: {
          label: 'Is this a current or former nationality?',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText:
            'Select “current” if you hold this nationality now, or “former” if you held this nationality in the past',
        },
        OtherNationalityNumber: {
          helpText: 'Including current and former nationalities/citizenships',
          label: 'How many other nationalities?',
          mandatory: true,
          visible: true,
        },
        BirthCountry: {
          label: 'Country of Birth',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'The country you were born in',
        },
        Gender: {
          label: 'Gender',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'As per your passport',
        },
        DOB: {
          label: 'Date of Birth',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'As per your passport.',
        },
        LivedInUKLast12Months: {
          helpText:
            'Select "Yes" if on the date of your visa application, you have lived in the UK for 12 months or more without any gaps. Otherwise, select "No"',
          label: 'Have you lived in the UK continuously for over 12 months?',
          subLabel: '',
          mandatory: true,
          visible: true,
        },
      },
      Page3: {
        title: 'Contact and Address',
        hidden: false,
        PhoneNumber: {
          label: 'Phone Number',
          subLabel: 'Mobile number preferred',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        HomeAddress: {
          label: 'Permanent Residential Address',
          subLabel: 'Address (e.g. unit 33, 17 Example Street)',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'Your current home / residential address e.g. Where you live',
        },
        City: {
          label: 'City',
          visible: true,
          mandatory: true,
          defaultValue: '',
        },
        Postcode: {
          label: 'Postcode (if applicable)',
          visible: true,
          mandatory: false,
          defaultValue: '',
        },
        Country: {
          label: 'Country',
          visible: true,
          mandatory: true,
          defaultValue: '',
        },
      },
      Page4: {
        title: 'Criminal Convictions',
        hidden: false,
        HasCriminalConvictions: {
          label: 'Have you ever had any criminal convictions, charges, or penalties in the UK or any other country?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText:
            "Please respond 'yes' if you currently have any of the following: a criminal conviction, a penalty for a driving offense (e.g. speeding or driving without insurance), an ongoing arrest or charge awaiting trial, a caution, warning, reprimand, or another out-of-court penalty, a civil court judgment (e.g. for non-payment of debt, bankruptcy, or antisocial behavior), or a civil penalty.",
        },
        CriminalConvictionsReason: {
          label: 'Please provide more information on the date and nature of the offence(s)',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
      },
      Page5: {
        title: 'Student Number and Course',
        hidden: false,
        StudentNumber: {
          label: 'Student Number',
          subLabel: 'All details are as per your offer letter',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        CourseLevel: {
          label: 'What is the level of your course?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        CourseLevelOther: {
          defaultValue: '',
          helpText: '',
        },
        AutoCourseName: {
          label: 'Course Name',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'Please select the same course name as listed on your offer letter',
        },
        ManualCourseName: {
          label: 'Course Name',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'As per your offer letter',
        },
        Campus: {
          label: 'Select your campus',
          subLabel: '',
          visible: false,
          mandatory: false,
          defaultValue: '',
          helpText: 'As per your offer letter',
        },
        CourseFee: {
          label: 'First Year Course Fee',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: null,
          helpText: '',
        },
        CourseScholarshipAmount1: {
          subLabel: 'As stated in your offer letter ',
          visible: true,
          mandatory: false,
          defaultValue: null,
          helpText:
            ' If you have been awarded any additional scholarships or discounts that are not listed on this page, you will be able to add them later',
        },
        DepositAmount: {
          helpText:
            'Please note if you have deposited less than the minimum deposit of 50% first year tuition fee you will need to make another transfer to make up the difference before your CAS can be issued',
        },
        DepositPaid: {
          helpText:
            "<span>Answer 'yes' if you have paid a contribution towards your course fees. <br /><br />A minimum deposit of 50% first year tuition fee must be made before the university can process your CAS.</span>",
        },
        StartDate: {
          label: 'Course Start Date',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: null,
          helpText: '',
        },
        EndDate: {
          label: 'Course End Date',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: null,
          helpText: '',
        },
        CAH3: {
          label: 'CAH3 Code',
          subLabel: '',
          visible: true,
          mandatory: false,
          defaultValue: '',
          helpText: 'If present on your offer letter (e.g. CAH10-01-02)',
        },
        CASReceived: {
          label: 'Have you received your CAS?',
          subLabel: '',
          visible: false,
          mandatory: false,
          defaultValue: '',
          helpText: '',
        },
        IsWorkPlacement: {
          label: 'Work Placement',
          helpText: 'Select "yes", if your course includes a work placement',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
        },
      },
      Page5_1: {
        title: 'Evidence Used to Obtain Offer',
        hidden: true,
        OfferEvidence: {
          label: '',
          subLabel: 'Please make your corrections to the text or add a note at the bottom',
          visible: true,
          mandatory: true,
          helpText: '',
        },
      },
      Page6: {
        title: 'English Test',
        HasEnglishTest: {
          label: 'Were you required to submit an English test as part of your application?',
          visible: true,
          mandatory: true,
          defaultValue: undefined,
          helpText: '',
        },
        EnglishTestType: {
          label: 'What type of English test?',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        EnglishTestTypeOther: {
          defaultValue: '',
          label: 'What is the name of the English test you completed?',
          visible: true,
          mandatory: true,
          helpText: '',
        },
        EnglishTestNotReqdReason: {
          label: 'How did you meet the English language requirement?',
          visible: true,
          mandatory: true,
        },
        EnglishTestNotReqdReasonOther: {
          label: 'Please explain',
          visible: true,
          mandatory: true,
        },
        Qualification: {
          label: 'Which qualification?',
          visible: true,
          mandatory: true,
        },
        QualificationOther: {
          label: 'What is the name of the qualification?',
          visible: true,
          mandatory: true,
        },
      },
      Page7: {
        title: 'Passport Details',
        hidden: false,
        PassportNumber: {
          label: 'Passport Number',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: ``,
        },
        PassportStatus: {
          label: 'Do you currently have a passport?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        PassportExpiryDate: {
          label: 'Passport Expiry Date',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText:
            'Note, if your passport expires within 6 months of your course start date, a new passport copy will be requested later. If you are unable to provide the new passport information, please contact the admissions team.',
        },
        PassportExpectedDate: {
          label: 'When do you expect to receive your passport?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'Enter the approximate date you expect to receive your new passport.',
        },
      },
      Page8: {
        title: 'Enrolment Support',
        hidden: false,
        AgentNameConfig: {
          label: 'Who is helping you?',
          mandatory: true,
          visible: true,
        },
        AutoAgentName: {
          label: 'What is the name of your agency/agent?',
          mandatory: true,
          visible: true,
        },
        ManualAgentName: {
          label: 'Manually Enter Agent Name',
          mandatory: true,
          visible: true,
        },
        WithAgent: {
          label: 'Did anyone help you submit your application to study with us?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'An "Agent" is a local office or business that helps students choose and apply to universities',
        },
      },
      Page9: {
        title: 'Visa Support',
        hidden: false,
        VisaSupport: {
          helpText:
            'This service is completely free, and we’re here to make your journey to studying abroad as simple and stress-free as possible.',
          label: 'Would you like free professional support with your visa application and arrival planning?',
        },
      },
      Page10: {
        title: 'WhatsApp',
        hidden: false,
        WhatsApp: {
          helpText: '',
        },
        WhatsAppPhoneNumber: {
          helpText: '',
        },
      },
    },
    History: {
      Page1: {
        title: 'Prior Study and Travel',
        LastCourse: {
          helpText:
            'Please choose the option that accurately represents the course you have either completed most recently or are expected to complete by the time you apply for your visa.',
        },
        LastCourseOther: {
          helpText: '',
        },
        LastCourseYear: {
          helpText:
            'Please enter the year you graduated from, or will graduate from, the course you just declared in the previous question.',
        },
        HasOverseasTravel: {
          defaultValue: undefined,
          helpText: 'Includes any country outside the border of the country you currently live in',
        },
        OverseasTravelTimes: {
          helpText: '',
        },
        HasUKTravel: {
          defaultValue: undefined,
          helpText: '',
        },
        UKTravelTimes: {
          helpText: '',
        },
        HasPreviousUkStudy: {
          defaultValue: undefined,
          visible: true,
          helpText: '',
        },
        HasPreviousUkStudyVisa: {
          defaultValue: undefined,
          visible: true,
          helpText: '',
        },
        PreviousUkStudyVisaType: {
          helpText: '',
        },
        PreviousUkStudyLevel: {
          helpText: '',
        },
        PreviousUkStudyYearOfReceived: {
          helpText: '',
        },
        PreviousUkStudyCourseComplete: {
          helpText: '',
        },
        PreviousUkStudyReasonNotCompleteCourse: {
          helpText: '',
        },
        PreviousUkStudyIncopleteRecorded: {
          helpText: '',
        },
        PreviousUkStudyVisaShortened: {
          helpText: '',
        },
        PreviousUkStudyVisaShortenedDate: {
          helpText: '',
        },
        LastCourseLevel: {
          helpText: 'Relates only to the last academic course you have studied in the UK on a Tier 4 visa',
        },
        LowerOrSameLevelReason: {},
      },
      Page2: {
        title: 'Prior Travel and Refusals',
        PrevUKOtherVisa: {
          helpText:
            'This question relates to any non-student UK visas, which are typically represented as stickers or vignettes in your passport, granting you permission to enter the country. Standard entry stamps should not be included in your response.',
        },
        PrevUKOtherVisaType: {
          helpText: 'Select "Other" if you can\'t find your visa type',
        },
        PrevUKOtherVisaYear: {
          helpText: '',
        },
        UKLeaveToRemain: {
          helpText: 'Have you ever applied to live permanently in the UK',
        },
        PrevRejections: {
          helpText: 'For example at a UK airport or seaport',
        },
        PrevRejectionReason: {
          helpText: '',
        },
        PrevVisaRefusal: {
          helpText: `
            <span>
              This question relates to UK visa refusals only.
              <br />
              <br />A visa refusal is a letter or email issued by a government after a
              visa application. A refusal denies your request to enter the country.
            </span>
          `,
        },
        PrevVisaRefusalType: {
          helpText: "Select other if you can't find your visa type",
        },
        PrevVisaRefusalYear: {
          helpText: '',
        },
        PrevNonUKVisaRefusal: {
          helpText: `
            <span>
              This question relates to any visa refusals from countries other than the
              UK.
              <br />
              <br />A visa refusal is a letter or email issued by a government after a
              visa application. A refusal denies your request to enter the country.
            </span>
          `,
        },
        PrevNonUKVisaRefusalCountry: {
          helpText: '',
        },
        PrevNonUKVisaRefusalStudent: {
          helpText: '',
        },
        PrevNonUKVisaRefusalYear: {
          helpText: '',
        },
      },
      Page3: {
        title: 'Travellers and Dependants',
        HomeCountryApplication: {
          helpText:
            'Please confirm whether you intend to submit your student visa application in the country you have declared as your nationality above. This question refers to the location of your visa application',
        },
        HomeCountryApplicationReason: {
          helpText: '',
        },
        FamilyMembersTravelling: {
          helpText:
            'This question refers to whether you will be coming to the UK alone or plan to bring any dependants or family members with you. <br><br> For the latest rules and regulations regarding students bringing dependants, please refer to the latest government guidance on the <a class="studentAppLinks" href="https://www.gov.uk/student-visa/family-members" target="_blank">gov.uk website</a>.',
        },
        SpouseTravelling: {
          helpText: '',
        },
        ChildrenTravelling: {
          helpText: '',
        },
        ChildrenTravellingNumber: {
          helpText: '',
        },
        PartnerVisaStatus: {
          helpText: '',
        },
      },
      title: 'Section 2 - Your History',
      shortTitle: 'Your History',
    },
    Consents: {
      SectionStart: {
        warning:
          'In this section you will acknowledge your responsibilities as a student and confirm relevant consents for your CAS application',
      },
      Page1: {
        title: 'Consent 1',
        hidden: false,
        heading: 'Accuracy of information provided by you',
        blurb: `Please ensure the information you have provided is correct and be sure to fill in any missing information.
            <br />
            <br />
              The University will use this information to obtain your CAS number from the UKVI which, when received by the University, will be sent to the email address you have provided.
            <br />
            <br />
              If a visa application is made with a CAS which has incorrect information, this could lead to the application being refused, immigration permission of the wrong length or with the wrong conditions being granted.
              <br />
              <br />
              I confirm that the information provided in the form is correct.
        `,
      },
      Page2: {
        blurb: `The Admissions / Compliance team at The University will only conduct checks they deem necessary and relevant as part of their due diligence checks as sponsor licence holders. I hereby authorise The University to contact UKVI to notify them of your student visa application and request an immigration history check and any other information deemed necessary to further my case.<br /><br /> If I am required to record an interview, I understand that attending this interview and providing my biometric information are required for my admission process to the University. I explicitly consent that Enroly Ltd. and its authorised sub-processors, acting pursuant the instructions of the University, collects my biometric information during the interview and compares this information against the biometric data in my passport for the purposes of confirming my identity.<br /><br /> I understand that artificial intelligence is used to support identity verification and to assist trained staff in evaluating my interview responses. With respect to the above, I acknowledge that my information will not be profiled or cumulatively scored, and each of my responses is evaluated independently to ensure fairness.<br /><br /> I further understand that all AI assessments are reviewed by trained staff, and the University makes all final admission decisions independently.`,
        heading:
          'Consent to contact UKVI, process biometric information, and use AI for identity verification and assessment',
        hidden: false,
        title: 'Consent 2',
      },
      Page3: {
        blurb: `You previously indicated you would like access to university approved help with your visa preparation and application.<br /><br />This support is free and is intended to offer a guided visa application experience. It will be provided by SRA and/or OISC regulated specialist visa support partners. In order to provide you with support for your visa preparation and application our visa support partners and the third parties that provide administrative support services to them will require access to the information provided by you within this system.<br /><br />Do you consent to the sharing of the personal information you provided in the system during your application process with SRA and/or OISC regulated specialist visa support partners or their administrative support partners for visa preparation and application purposes?\n`,
        blurbMandatory: `As part of your enrolment you are eligible to attend university-approved help for your visa preparation and application.<br /><br />The support is free and is intended to offer a guided visa application experience. It will be provided by SRA and/or OISC regulated specialist visa support partners. In order to provide you with support for your visa preparation and application our visa support partners and the third parties that provide administrative support services to them will require access to the information provided by you within this system.<br /><br />This support is required by the university as part of your enrolment process. If you do not accept it, the university will not be able to complete your enrolment to the course(s) of your choice.<br /><br />Do you consent to the sharing of the personal information you provided in the system during your application process with SRA and/or OISC regulated specialist visa support partners or their administrative support partners for visa preparation and application purposes?\n`,
        heading: 'Consent to share provided information with visa support partner',
        hidden: false,
        title: 'Consent 3',
      },
      Page4: {
        title: '',
        blurb: '',
        externalLinkInstruction: '',
        externalLink: '',
        externalLinkText: undefined,
        hidden: true,
      },
      title: 'Section 3 - Your Responsibilities',
      shortTitle: 'Your Responsibilities',
    },
    Finances: {
      Page1: {
        title: 'Source of Funds and Calculator',
        FundingSources: {
          helpText: '',
        },
        AmountToProve: {
          helpText: 'To obtain your visa you will need to have access to the funds above',
        },
        RemainingAmountToProve: {
          helpText: 'You cannot proceed until you have proven how you will fund your studies and maintenance costs',
        },
        CoverType: {
          label: 'How much is your sponsor (or scholarship) providing for your tuition fees?',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        HasSeparateLivingCost: {
          label: 'Are your living costs listed separately from your tuition in your sponsorship documentation?',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        MonthlyOrUpfrontLivingCost: {
          label:
            'Will your living costs be provided to you as a monthly stipend / payment or provided to you as an upfront payment?',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        TotalLivingCost: {
          label: 'What is the total amount for living costs?',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        MonthlyLivingCost: {
          label: 'What is the monthly amount for living costs?',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        AmountCovered: {
          helpText: 'The amount in pounds that will be used from this account',
        },
        AmountCoveredBankSponsor: {
          helpText: `
            Please remember to allow for currency fluctuations that may occur during
            the enrolment process. To do this you may want to enter up to 25% more
            than is required at today’s exchange rate.
            <br />
            <br />
            Entering more money here will not affect how much you will pay during
            your studies.
          `,
        },
        TotalAmountInAccount: {
          helpText: `
              If your account is not in GBP, use the latest exchange rate value to convert your currency to GBP.
              UKVI use <a class="studentAppLinks" href="https://www.oanda.com/currency-converter/en/?from=EUR&to=GBP&amount=1" target="_blank">Oanda</a> exchange rate.
          `,
          subLabel: 'Enter the amount currently in your account to the nearest pound sterling (GBP).',
        },
        FundsMatured: {
          helpText: 'Has the full amount of required funds been in this account for 28 days?',
        },
        FundsMatureDate: {
          helpText: 'What date will the funds have been in this account for 28 days?',
        },
        ScholarshipName: {
          helpText: 'Enter the name / type of scholarship you received ',
        },
        ScholarshipNature: {
          helpText: 'How much of your course fee and/or living expenses does your scholarship cover?',
        },
        CompanyPresence: {
          helpText: 'e.g. Does it operate outside of its home country',
        },
        EducationLoanName: {
          helpText: `Select the person who's name is on the loan. If your name is on the loan select “my name”.`,
        },
        RelationshipToPerson: {
          helpText: '',
        },
        OtherFundingDesc: {
          helpText: 'Explain in your own words, maximum 2 sentences',
        },
        DepositPaid: {
          label: 'Have you paid a course fee deposit?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText:
            "<span>Answer 'yes' if you have paid a contribution towards your course fees.<br /><br />A minimum deposit of £3000 must be made before the university can process your CAS.</span>",
        },
        DepositAmount: {
          label: 'Deposit Amount',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: 0,
          helpText:
            'Enter the total deposit you have transferred to the university in GBP (british pounds). Please note if you have deposited less than the minimum deposit of £3000 you will need to make another transfer to make up the difference before your CAS can be issued.',
        },
        DepositDate: {
          label: 'When do you expect to pay your deposit?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: 'Tell us the approximate date you plan to make your deposit payment',
        },
        DepositReason: {
          label: "Please provide more information on why a deposit isn't required",
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        AccommodationDepositPaid: {
          label: 'Have you paid a deposit to the university for university accommodation?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        AccommodationDepositAmount: {
          label: 'Accommodation Deposit Amount',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: 0,
          helpText: '',
        },
        AccommodationDepositDate: {
          label: 'Approximately when do you plan on paying your accommodation deposit?',
          subLabel: '',
          visible: true,
          mandatory: true,
          defaultValue: '',
          helpText: '',
        },
        CanWithdrawImmediately: {
          label: 'Can the funds in this account be accessed immediately?',
        },
      },
      title: 'Section 4 - Your Financial Plan',
      shortTitle: 'Finance',
    },
    Summary: {
      Incomplete: {
        title: "You've reached the end of the CAS Checklist",
      },
      Complete: {
        title: 'CAS Checklist Complete!',
        warning: 'Continue to your task list for document upload and a Pre-CAS Questionnaire',
      },
    },
  },
  Interview: {
    SectionStart: {
      title: 'Pre-CAS Questionnaire',
    },
    SectionComplete: {
      title: 'Pre-CAS Questionnaire Complete!',
      warning: 'Go to task list to complete the remaining items',
    },
    Page1: {
      WhyUK: {
        helpText: 'Select the option that best describes your reason for studying in the UK',
      },
      WhyUKOther: {
        helpText: 'Maximum 2 sentences',
      },
      CourseReasons: {
        helpText: 'You can select more than one option',
      },
      CourseReasonsOther: {
        helpText: 'Maximum 2 sentences',
      },
      UniReasons: {
        helpText: 'You can select more than one option',
      },
      UniReasonsOther: {
        helpText: 'Maximum 2 sentences',
      },
      UniChoiceReason1: {
        helpText: 'Maximum 2 sentences per reason',
      },
      UniChoiceReason2: {
        helpText: 'Maximum 2 sentences per reason',
      },
      UniChoiceReason3: {
        helpText: 'Maximum 2 sentences per reason',
      },
    },
    Page2: {
      Campus: {
        helpText: 'If you are not sure please refer to your offer letter or the university website',
      },
      ConsiderOtherUni: {
        helpText:
          'Entering other universities here will not affect your application, we encourage students to be well researched',
      },
      UniResearchMethod: {
        helpText: 'You can select more than one option',
      },
      ConsiderOtherUniReason: {
        helpText: '',
      },
      ConisderOtherCourseName: {
        helpText: 'Please enter the title of the course you have considered',
      },
      ConsiderOtherCourseReason: {
        helpText: 'Explain in your own words',
      },
      CourseAlignCareerReason: {
        helpText: 'Explain in your own words, maximum 2 sentences',
      },
    },
    Page3: {
      WorkWhileStudy: {
        helpText: 'This refers to working part time',
      },
      WorkWhileStudyReason: {
        helpText: 'Explain in your own words, maximum 3 sentences',
      },
      CourseAndLivingWithoutWorking: {
        helpText: 'It is important there is nothing hindering your ability to pay your fees or living expenses',
      },
      CourseAndLivingWithoutWorkingReason: {
        helpText: 'Explain in your own words, maximum 3 sentences',
      },
      FurtherStudyCareerPlan: {
        helpText: 'Explain in your own words, maximum 3 sentences',
      },
    },
    Page4: {},
  },
  reminderNotifications: JSON.parse(JSON.stringify(reminderNotificationsConfig)),
  enrolyAccess: '',
  applyEnabled: false,
  faculties: [],
  departments: [],
  categories: [],
};

const initialState = {
  ...defaultConfig,
};

const reducer: Reducer<IStudentConfig> = (state = initialState, action) => {
  const overwriteMerge = (_: [], sourceArray: []) => sourceArray;
  switch (action.type) {
    case StudentActionTypes.GET_APPLICATION_RECORD_SUCCESS:
    case StudentActionTypes.GET_STUDENT_RECORD_SUCCESS:
      let newState = { ...state };
      if (action.payload?.config) {
        const config: any = action.payload.config || {};
        newState = deepmerge(defaultConfig, config, {
          arrayMerge: overwriteMerge,
        });
      }
      if (action.payload?.uniConfig) {
        const uniConfig: any = action.payload.uniConfig || {};
        newState = deepmerge(newState, uniConfig, {
          arrayMerge: overwriteMerge,
        });
      }
      return newState;

    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS: {
      if (action.payload?.regionConfig) {
        let regionConfig: any = { ...state, ...action.payload?.regionConfig };
        const overwriteMerge = (_: [], sourceArray: []) => sourceArray;
        regionConfig = deepmerge(state, regionConfig, {
          arrayMerge: overwriteMerge,
        });
        return regionConfig;
      }
      return state;
    }
    default:
      return state;
  }
};

export { reducer as StudentConfigReducer };
